import { SetStateAction, useEffect, useState } from "react";
import { DatePicker, useDatePickerHelpers, Input } from "@newsela/angelou";
import { isDateValid } from "../../utils";
import { IReturnCitationPublication } from "../../CitationAction.interface";
import {
  datePickerPublicationCss,
  formBlockWithoutLineCss,
  formRowCss,
  inputPublicationCss,
  subTitleCss,
} from "./CitationEditFormPublication.style";
import type { DateValue } from "@internationalized/date";

interface CitationEditFormPublicationProps {
  websiteTitle: string | null;
  url: string | null;
  publishedDate: string;
  touched: boolean;
  handlePublicationReturn: (data: IReturnCitationPublication) => void;
}

const CitationEditFormPublication = ({
  publishedDate,
  touched,
  websiteTitle,
  url,
  handlePublicationReturn,
}: CitationEditFormPublicationProps) => {
  const { parseAbsolute } = useDatePickerHelpers();

  const date = new Date();
  const isoDate = date.toISOString();

  const [dateAccessedValue, setDateAccessedValue] = useState<string>(isoDate);
  const [datePublishedValue, setDatePublishedValue] = useState<string>(isoDate);
  const [websiteTitleValue, setWebsiteTitleValue] = useState<string>(
    websiteTitle || "",
  );
  const [urlValue, setUrlValue] = useState<string>(url || "");

  useEffect(() => {
    handlePublicationReturn({
      dateAccessed: dateAccessedValue,
      datePublished: datePublishedValue,
      websiteTitle: websiteTitleValue,
      url: urlValue,
    });
  }, [dateAccessedValue, datePublishedValue, websiteTitleValue, urlValue]);

  useEffect(() => {
    const isValidDate = isDateValid(publishedDate);
    if (isValidDate && datePublishedValue !== publishedDate)
      setDatePublishedValue(publishedDate);
  }, [publishedDate]);

  const handleDatePicker = (
    dateValue: SetStateAction<DateValue>,
    type: string,
  ) => {
    const newDate = new Date(dateValue.toString().replace(/\[undefined]/g, ""));
    const newIsoDate = newDate.toISOString();

    if (type === "published") setDatePublishedValue(newIsoDate);
    else setDateAccessedValue(newIsoDate);
  };

  return (
    <div css={formBlockWithoutLineCss}>
      <h4 css={subTitleCss}>Online publication info</h4>
      <div css={formRowCss}>
        <Input
          label="Website title"
          isRequired
          inputType={Input.inputTypes.text}
          underInputId="publication-website-title"
          value={websiteTitleValue}
          onChange={(e) => setWebsiteTitleValue(e.target.value)}
          touched={touched}
          error={
            websiteTitleValue.length === 0 ? "Missing website title" : null
          }
          __cssFor={inputPublicationCss}
        />
        <Input
          label="URL"
          isRequired
          inputType={Input.inputTypes.text}
          value={urlValue}
          onChange={(e) => setUrlValue(e.target.value)}
          underInputId="publication-url"
          touched={touched}
          error={urlValue.length === 0 ? "Missing URL" : null}
          __cssFor={inputPublicationCss}
        />
        <DatePicker
          label="Date published"
          data-testid="CitationPublished-DatePicker"
          isRequired
          error={
            datePublishedValue === undefined ? "Missing date published" : ""
          }
          onChange={(event) => handleDatePicker(event, "published")}
          touched={touched}
          value={parseAbsolute(datePublishedValue)}
          __cssFor={datePickerPublicationCss}
          verticalOffset="-400px"
        />
        <DatePicker
          label="Date accessed"
          data-testid="CitationAccessed-DatePicker"
          isRequired={false}
          onChange={(event) => handleDatePicker(event, "accessed")}
          touched={touched}
          value={parseAbsolute(dateAccessedValue)}
          __cssFor={datePickerPublicationCss}
          verticalOffset="-400px"
        />
      </div>
    </div>
  );
};

export default CitationEditFormPublication;
